'use client';

import { ReactNode, useEffect } from 'react';
import { usePathname } from 'next/navigation';

function afterIdle(callback: () => void, timeout = 5000): () => void {
  if (window.requestIdleCallback) {
    const id = window.requestIdleCallback(callback, { timeout });
    return () => window.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(callback, timeout);
  return () => {
    window.clearTimeout(id);
  };
}

export default function AnalyticsProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname) return;
    let active = true;

    void import('@/lib/analytics/core').then(({ markInitialBeaconPath, pushPageView }) => {
      if (!active) return;
      markInitialBeaconPath();
      pushPageView(pathname);
    });

    return () => {
      active = false;
    };
  }, [pathname]);

  // GA4-A3: capture URL attribution as early as possible (first mount), not on
  // the 3–8s idle path below, so a fast paid bounce with ?gclid=/utm_* lands in
  // the bizrnr_attrib cookie before it leaves. captureAttribution is idempotent.
  useEffect(() => {
    if (!pathname) return;
    let active = true;
    void import('@/lib/attribution/capture').then(({ captureAttribution }) => {
      if (active) captureAttribution();
    });
    return () => {
      active = false;
    };
  }, [pathname]);

  useEffect(() => {
    if (!pathname) return;
    let active = true;
    let cancelIdle: (() => void) | undefined;
    const isHomepage = pathname === '/';
    const delay = isHomepage ? 7000 : 12000;
    const timeout = window.setTimeout(() => {
      cancelIdle = afterIdle(() => {
        if (!active) return;
        void Promise.all([
          import('@/lib/attribution/capture'),
          import('@/lib/attribution/ai-referral'),
        ]).then(([attribution, aiReferral]) => {
          if (!active) return;
          // captureAttribution now runs eagerly on mount (GA4-A3); the idle
          // path only needs the network touchpoint.
          void attribution.recordTouchpoint();
          aiReferral.detectAiReferral();
        });
      }, isHomepage ? 8000 : 3000);
      if (!active) cancelIdle();
    }, delay);

    return () => {
      active = false;
      window.clearTimeout(timeout);
      cancelIdle?.();
    };
  }, [pathname]);

  useEffect(() => {
    if (!pathname) return;
    let active = true;
    let cancelIdle: (() => void) | undefined;
    let unregister: (() => void) | undefined;
    const delay = pathname === '/' ? 9000 : 15000;
    const timeout = window.setTimeout(() => {
      cancelIdle = afterIdle(() => {
        if (!active) return;
        void import('@/lib/analytics/enhanced-events').then(({ registerEnhancedEvents }) => {
          if (!active) return;
          unregister = registerEnhancedEvents();
        });
      }, 8000);
      if (!active) cancelIdle();
    }, delay);

    return () => {
      active = false;
      window.clearTimeout(timeout);
      cancelIdle?.();
      unregister?.();
    };
  }, [pathname]);

  return <>{children}</>;
}
